/**
 * Template-driven companion bed designer.
 *
 * Replaces the old algorithmic engine. Given:
 *   - a chosen GuildTemplate
 *   - the bed dimensions
 *   - a density (sparse | balanced | packed)
 *   - the user's "missing companion" decisions (skip / replace / add)
 *
 * …produces a deterministic layout with placements + plan + rationale,
 * compatible with the existing BedDiagram component and `companion_designs` row.
 */

import type { BedSpec, BedDesign, PlacedPlant, PlanItem } from './companionPlanting';
import {
  GuildTemplate,
  TemplateSlot,
  DENSITY_MULTIPLIER,
} from './companionTemplates';

export type Density = 'sparse' | 'balanced' | 'packed';

export interface MissingDecision {
  /** plant name from template */
  name: string;
  /** 'add' = treat as available, 'skip' = build without */
  decision: 'add' | 'skip';
}

const TRANSPLANT_ONLY = new Set([
  'Tomatoes', 'Tomato', 'Peppers', 'Pepper', 'Chillies', 'Chili', 'Aubergine',
  'Cucumber', 'Cucumbers', 'Courgette', 'Courgettes', 'Squash', 'Pumpkin', 'Pumpkins',
  'Cabbage', 'Broccoli', 'Cauliflower', 'Brussels Sprouts', 'Kale',
  'Leeks', 'Basil', 'Sweetcorn',
]);

function transplantMethod(name: string): 'direct' | 'transplant' {
  return TRANSPLANT_ONLY.has(name) ? 'transplant' : 'direct';
}

function layerOf(role: TemplateSlot['role']): PlacedPlant['layer'] {
  switch (role) {
    case 'anchor':
    case 'climber':
      return 'tall';
    case 'interplant':
      return 'mid';
    case 'sprawler':
    case 'border':
      return 'edge';
    case 'corner':
    case 'trap':
      return 'edge';
    default:
      return 'mid';
  }
}

function roundQty(perSqM: number, areaM2: number, density: Density): number {
  const raw = perSqM * areaM2 * DENSITY_MULTIPLIER[density];
  return Math.max(1, Math.round(raw));
}

interface ResolvedSlot {
  slot: TemplateSlot;
  qty: number;
  legendIndex: number;
}

/**
 * Build the design.
 */
export function buildDesignFromTemplate(opts: {
  bed: BedSpec;
  template: GuildTemplate;
  density: Density;
  /** Plant names the user is actively growing (case-insensitive). */
  userPlantNames: string[];
  /** Decisions on missing companions (only relevant for non-required slots). */
  missingDecisions: MissingDecision[];
  /** Hero plant the user picked (case-insensitive). */
  heroPlant: string;
}): BedDesign {
  const { bed, template, density, userPlantNames, missingDecisions, heroPlant } = opts;

  const have = new Set(userPlantNames.map((n) => n.toLowerCase()));
  const decisionMap = new Map(
    missingDecisions.map((d) => [d.name.toLowerCase(), d.decision] as const),
  );

  // Normalise bed orientation: long side horizontal.
  const L = Math.max(bed.length_cm, bed.width_cm);
  const W = Math.min(bed.length_cm, bed.width_cm);
  const layoutBed: BedSpec = { ...bed, length_cm: L, width_cm: W };
  const areaM2 = (L * W) / 10000;

  // 1. Resolve which slots actually go in this bed.
  //    - The chosen HERO is identified by NAME (not role) — it may be a climber
  //      (e.g. Cucumber Tower) or sprawler (Squash Sprawl), not just an anchor.
  //    - Other slots: include if user has that plant OR user said "add".
  //    - Skip alternative anchors that aren't the hero.
  const heroLower = heroPlant.toLowerCase();
  const heroSlot =
    template.slots.find((s) => s.name.toLowerCase() === heroLower)
    ?? template.slots.find((s) => s.role === 'anchor');

  const resolved: ResolvedSlot[] = [];
  let legend = 0;

  const includeSlot = (s: TemplateSlot) => {
    legend += 1;
    resolved.push({ slot: s, qty: roundQty(s.perSqM, areaM2, density), legendIndex: legend });
  };

  // Hero first (always included)
  if (heroSlot) includeSlot(heroSlot);

  // Other slots
  for (const s of template.slots) {
    if (s === heroSlot) continue;
    // For anchor-role alternatives that AREN'T the hero, skip unless user is also growing them.
    // (Prevents e.g. Sunflowers dominating a Cucumber bed when cucumber is hero.)
    if (s.role === 'anchor') {
      if (have.has(s.name.toLowerCase())) includeSlot(s);
      continue;
    }
    // Required → always include.
    if (s.required) {
      includeSlot(s);
      continue;
    }
    // Optional: include if user has it or chose "add".
    if (have.has(s.name.toLowerCase())) {
      includeSlot(s);
      continue;
    }
    const decision = decisionMap.get(s.name.toLowerCase());
    if (decision === 'add') {
      includeSlot(s);
    }
  }

  // ---- Realism cap: HERO is the crop, companions are accents.
  // Target: hero ends up as 60-70% of total plants in the bed.
  // Companions are capped as a fraction of the hero count.
  const heroResolved = resolved.find((r) => r.slot === heroSlot);
  if (heroResolved) {
    // Boost hero qty for low-density heroes (sprawlers like squash at 1/m²)
    // so they aren't numerically swamped by 4/m² interplants.
    // We want hero >= ~2x the largest companion's natural qty as a starting point.
    const companions = resolved.filter((r) => r !== heroResolved);
    const maxCompanionNatural = Math.max(1, ...companions.map((r) => r.qty));
    const heroFloor = Math.max(heroResolved.qty, Math.ceil(maxCompanionNatural * 1.5));
    heroResolved.qty = heroFloor;

    const heroQty = heroResolved.qty;
    // Tightened caps — companions stay accents.
    const COMPANION_CAP_FRAC: Record<TemplateSlot['role'], number> = {
      anchor: 0.5,      // secondary anchors no longer rival the hero
      climber: 0.4,
      interplant: 0.35, // basil/parsley etc
      sprawler: 0.3,
      border: 0.5,      // edging can be a touch more
      corner: 0.2,
      trap: 0.25,
    };
    for (const r of resolved) {
      if (r === heroResolved) continue;
      const frac = COMPANION_CAP_FRAC[r.slot.role] ?? 0.4;
      const cap = Math.max(2, Math.round(heroQty * frac));
      if (r.qty > cap) r.qty = cap;
    }
  }

  // 2. POLYCULTURE LAYOUT — fill the whole bed with an aesthetic mosaic.
  //    - Build a jittered grid sized to fill the entire inner bed.
  //    - Density controls icon count AND icon size (denser = more, smaller).
  //    - Role zones bias placement (anchors back, borders front, etc.).
  //    - Species are interleaved round-robin so neighbours differ.
  const margin = 18;
  const placements: PlacedPlant[] = [];

  const anchors = resolved.filter((r) => r.slot.role === 'anchor');
  const climbers = resolved.filter((r) => r.slot.role === 'climber');
  const corners = resolved.filter((r) => r.slot.role === 'corner' || r.slot.role === 'trap');

  const x0 = margin;
  const y0 = margin;
  const x1 = L - margin;
  const y1 = W - margin;
  const innerW = x1 - x0;
  const innerH = y1 - y0;

  // Density-aware target icon count.
  // The "sparse" look (14/m²) was the sweet spot that filled beds nicely
  // without overcrowding — that becomes our PACKED baseline. Balanced and
  // sparse step down from there with progressively bigger icons.
  const ICONS_PER_M2: Record<Density, number> = {
    sparse: 7,
    balanced: 10,
    packed: 14,
  };
  const targetIcons = Math.max(10, Math.round(areaM2 * ICONS_PER_M2[density]));

  // Grid sized to fill bed with ~targetIcons cells
  const cellArea = (innerW * innerH) / targetIcons;
  const cellSize = Math.sqrt(cellArea);
  const cols = Math.max(3, Math.round(innerW / cellSize));
  const rows = Math.max(2, Math.round(innerH / cellSize));
  const cellW = innerW / cols;
  const cellH = innerH / rows;

  // Icon size scales with cell — denser = smaller icons
  const baseRadius = Math.min(cellW, cellH) * 0.42;
  const radiusFor = (role: TemplateSlot['role']): number => {
    const mult = role === 'anchor' ? 1.15
      : role === 'sprawler' ? 1.1
      : role === 'climber' ? 0.95
      : role === 'border' ? 0.85
      : role === 'corner' || role === 'trap' ? 0.9
      : 1.0;
    return Math.max(10, Math.min(34, baseRadius * mult));
  };

  const makePlacement = (r: ResolvedSlot, x: number, y: number): PlacedPlant => ({
    plantId: r.slot.name,
    name: r.slot.name,
    fromUserGarden: false,
    x,
    y,
    radius: radiusFor(r.slot.role),
    layer: layerOf(r.slot.role),
    legendIndex: r.legendIndex,
  });

  // Distribute icon budget — HERO dominates, companions are accents.
  // Use linear qty weights (not sqrt) so the hero's larger count actually
  // shows on the diagram. Then enforce a minimum hero share.
  const totalCells = cols * rows;
  const budget = Math.min(totalCells, targetIcons);
  const weights = resolved.map((r) => r.qty);
  const wSum = weights.reduce((s, w) => s + w, 0) || 1;
  const desired = resolved.map((r, i) =>
    Math.max(1, Math.min(r.qty, Math.round((weights[i] / wSum) * budget))),
  );
  // Guarantee the hero gets ~65% of icons (user wants 60-70% hero dominance).
  const heroIdx = resolved.findIndex((r) => r.slot === heroSlot);
  if (heroIdx >= 0) {
    const minHeroShare = Math.min(resolved[heroIdx].qty, Math.ceil(budget * 0.65));
    if (desired[heroIdx] < minHeroShare) {
      // steal icons from largest non-hero allocations
      let need = minHeroShare - desired[heroIdx];
      const order = resolved
        .map((_, i) => i)
        .filter((i) => i !== heroIdx)
        .sort((a, b) => desired[b] - desired[a]);
      for (const i of order) {
        if (need <= 0) break;
        const take = Math.min(need, Math.max(0, desired[i] - 1));
        desired[i] -= take;
        need -= take;
      }
      desired[heroIdx] = minHeroShare;
    }
  }

  // Build cells with deterministic jitter
  type Cell = { col: number; row: number; cx: number; cy: number; idx: number };
  const cells: Cell[] = [];
  let idxC = 0;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const jx = (((c * 928371 + r * 12347) % 100) / 100 - 0.5) * cellW * 0.2;
      const jy = (((c * 31337 + r * 7919) % 100) / 100 - 0.5) * cellH * 0.2;
      cells.push({
        col: c,
        row: r,
        cx: x0 + cellW * (c + 0.5) + jx,
        cy: y0 + cellH * (r + 0.5) + jy,
        idx: idxC++,
      });
    }
  }

  // Every species spreads across the whole bed — no row banding.
  // Only the very front/back edges get a tiny role bias (borders to edges,
  // anchors slightly away from edges) so the polyculture reads as mixed,
  // not stratified. Corners are still reserved separately above.
  const edgeBiasScore = (cell: Cell, slot: TemplateSlot): number => {
    const rowFrac = rows <= 1 ? 0.5 : cell.row / (rows - 1);
    // distance from nearest edge (0 = edge, 0.5 = middle)
    const edgeDist = Math.min(rowFrac, 1 - rowFrac);
    switch (slot.role) {
      case 'border':
        // prefer edges (low edgeDist)
        return edgeDist * 0.3;
      case 'anchor':
        // mild preference away from front/back edges
        return Math.max(0, 0.15 - edgeDist) * 0.2;
      default:
        return 0;
    }
  };

  const cellScore = (cell: Cell, slot: TemplateSlot): number => edgeBiasScore(cell, slot);

  const used = new Set<number>();

  // Reserve corners for corner/trap species
  if (corners.length > 0) {
    const cornerSpots = [
      cells.find((c) => c.col === 0 && c.row === 0),
      cells.find((c) => c.col === cols - 1 && c.row === 0),
      cells.find((c) => c.col === 0 && c.row === rows - 1),
      cells.find((c) => c.col === cols - 1 && c.row === rows - 1),
    ].filter((c): c is Cell => !!c);
    const cornerQueue: ResolvedSlot[] = [];
    const counts = corners.map((c) => Math.min(desired[resolved.indexOf(c)], 4));
    while (cornerQueue.length < cornerSpots.length && counts.some((n) => n > 0)) {
      for (let i = 0; i < corners.length && cornerQueue.length < cornerSpots.length; i++) {
        if (counts[i] > 0) { cornerQueue.push(corners[i]); counts[i]--; }
      }
    }
    cornerQueue.forEach((slot, i) => {
      const cell = cornerSpots[i];
      placements.push(makePlacement(slot, cell.cx, cell.cy));
      used.add(cell.idx);
      const ri = resolved.indexOf(slot);
      desired[ri] = Math.max(0, desired[ri] - 1);
    });
  }

  // Interleaved queue (skip corner species — they're done).
  // Use Bresenham-style fractional stepping so each species is spread EVENLY
  // across the placement order. This prevents the hero (which has the highest
  // count) from being all that's left at the end and clumping in one region.
  const cornerSet = new Set(corners);
  const placeable = resolved
    .map((r, i) => ({ r, i, n: desired[i] }))
    .filter((x) => !cornerSet.has(x.r) && x.n > 0);
  const totalToPlace = placeable.reduce((s, x) => s + x.n, 0);
  const queue: ResolvedSlot[] = [];
  if (totalToPlace > 0) {
    // accumulator per species; each step we add 1 to every accumulator,
    // and whenever an accumulator >= totalToPlace, we emit one of that species
    // and subtract totalToPlace. This evenly distributes counts across the queue.
    const acc = placeable.map(() => 0);
    const placed = placeable.map(() => 0);
    for (let step = 0; step < totalToPlace; step++) {
      // pick the species whose (acc+n)/totalToPlace ratio is most "owed"
      let bestJ = -1;
      let bestDeficit = -Infinity;
      for (let j = 0; j < placeable.length; j++) {
        if (placed[j] >= placeable[j].n) continue;
        // expected placed by this step vs actual placed
        const expected = ((step + 1) * placeable[j].n) / totalToPlace;
        const deficit = expected - placed[j];
        if (deficit > bestDeficit) { bestDeficit = deficit; bestJ = j; }
      }
      if (bestJ < 0) break;
      queue.push(placeable[bestJ].r);
      placed[bestJ]++;
      acc[bestJ] = 0;
    }
  }

  // Place each queued species into best-scoring free cell, avoiding same-species clustering.
  // Shuffle cells deterministically so iteration order doesn't bias placements toward
  // top-left when scores tie (which previously caused hero to clump at the bottom).
  const free = cells.filter((c) => !used.has(c.idx));
  for (let i = free.length - 1; i > 0; i--) {
    const j = Math.floor(((i * 2654435761) % 1000) / 1000 * (i + 1));
    [free[i], free[j]] = [free[j], free[i]];
  }
  for (const r of queue) {
    if (free.length === 0) break;
    let bestK = 0;
    let bestScore = Infinity;
    for (let k = 0; k < free.length; k++) {
      const cell = free[k];
      let s = cellScore(cell, r.slot) * 100;
      for (const p of placements) {
        if (p.legendIndex !== r.legendIndex) continue;
        const dx = p.x - cell.cx;
        const dy = p.y - cell.cy;
        const d = Math.sqrt(dx * dx + dy * dy);
        // Strong spread: same-species ideally stay >2 cells apart
        const minD = cellW * 2.2;
        if (d < minD) s += (minD - d) * 1.5;
      }
      if (s < bestScore) { bestScore = s; bestK = k; }
    }
    const chosen = free.splice(bestK, 1)[0];
    used.add(chosen.idx);
    placements.push(makePlacement(r, chosen.cx, chosen.cy));
  }

  // Climbers: nudge ~30% toward nearest anchor for visual association
  if (climbers.length > 0 && anchors.length > 0) {
    const anchorIdxSet = new Set(anchors.map((a) => a.legendIndex));
    const climberIdxSet = new Set(climbers.map((c) => c.legendIndex));
    const anchorPositions = placements.filter((p) => anchorIdxSet.has(p.legendIndex));
    if (anchorPositions.length > 0) {
      for (const p of placements) {
        if (!climberIdxSet.has(p.legendIndex)) continue;
        let nearest = anchorPositions[0];
        let nd = Infinity;
        for (const a of anchorPositions) {
          const d = (a.x - p.x) ** 2 + (a.y - p.y) ** 2;
          if (d < nd) { nd = d; nearest = a; }
        }
        p.x = p.x + (nearest.x - p.x) * 0.3;
        p.y = p.y + (nearest.y - p.y) * 0.3;
      }
    }
  }

  // 3. Plan items
  const plan: PlanItem[] = resolved.map((r) => ({
    plantId: r.slot.name,
    name: r.slot.name,
    quantity: r.qty,
    spacing_cm: Math.round(Math.sqrt(10000 / (r.slot.perSqM * DENSITY_MULTIPLIER[density]))),
    fromUserGarden: have.has(r.slot.name.toLowerCase()),
    notes: noteFor(r.slot.name, r.qty, r.slot.role),
    legendIndex: r.legendIndex,
    sowMethod: transplantMethod(r.slot.name),
  }));

  // 4. Rationale
  const rationale: string[] = [template.summary];
  for (const r of resolved) {
    rationale.push(`${r.slot.name}: ${r.slot.why}`);
  }

  const warnings: string[] = [];
  if (template.minAreaM2 && areaM2 < template.minAreaM2) {
    warnings.push(
      `This guild really wants at least ${template.minAreaM2} m² — your bed is ${areaM2.toFixed(1)} m². Counts have been scaled but it'll be cosy.`,
    );
  }

  return {
    spec: layoutBed,
    bedId: bed.id,
    bedName: bed.name,
    placements,
    plan,
    rationale,
    warnings,
  };
}

function noteFor(name: string, qty: number, role: TemplateSlot['role']): string {
  const method = transplantMethod(name);
  if (role === 'climber') return `Plant ${qty} at the foot of the anchor — they'll climb upward.`;
  if (role === 'sprawler') return `${qty} on the sunny edge — let leaves spill out as living mulch.`;
  if (role === 'corner') return `Tuck ${qty} into the corners.`;
  if (role === 'trap') return `${qty} around the outer ring as a sacrificial trap crop.`;
  if (role === 'border') return `${qty} along the front edge.`;
  if (method === 'transplant') return `Plant out ${qty} young plants once the frost has passed.`;
  return `Sow ${qty} directly into the soil.`;
}

/** Determine missing companions for a template given user's garden. */
export function missingCompanions(
  template: GuildTemplate,
  userPlantNames: string[],
  heroPlant: string,
): TemplateSlot[] {
  const have = new Set(userPlantNames.map((n) => n.toLowerCase()));
  const heroLower = heroPlant.toLowerCase();
  return template.slots.filter((s) => {
    if (s.role === 'anchor') return false; // anchors not "missing"
    if (s.name.toLowerCase() === heroLower) return false;
    return !have.has(s.name.toLowerCase());
  });
}
